import { useState, type ChangeEvent } from "react";
import { Button, Form, Spinner, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSearchUsers } from "../../../hooks/queries/useSuperAdmin";
import { useSetUserRole } from "../../../hooks/mutations/useUserMutations";
import { useDebouncedValue } from "../../../hooks/useDebouncedValue";
import type { User } from "../../../types/domain";

function UserSection() {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebouncedValue(query, 300);
  const { data: results, isFetching, refetch } = useSearchUsers(
    { q: debouncedQuery, page: 0, size: 25 },
    debouncedQuery.length > 0,
  );

  const setRoleMutation = useSetUserRole();

  const setRole = async (user: User, newRole: "USER" | "ADMIN") => {
    await setRoleMutation.mutateAsync({ userId: user.id, role: newRole });
    refetch();
  };

  return (
    <div>
      <Form.Control
        type="text"
        placeholder={t("zakulisi__searchUser")}
        value={query}
        onChange={(e: ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
        className="mb-3"
      />
      {isFetching && <Spinner animation="border" size="sm" className="ms-2" />}
      {results && results.content.length > 0 && (
        <Table striped bordered hover size="sm" variant="dark">
          <thead>
            <tr>
              <th style={{ width: "5rem" }}>ID</th>
              <th>{t("username")}</th>
              <th style={{ width: "8rem" }}>{t("zakulisi__role")}</th>
              <th style={{ width: "8rem" }}>{t("zakulisi__playerId")}</th>
              <th style={{ width: "16rem" }}>{t("auditLog__action")}</th>
            </tr>
          </thead>
          <tbody>
            {results.content.map((user) => {
              const isSuperadmin = user.role === "SUPERADMIN";
              return (
                <tr key={user.id}>
                  <td>{user.id}</td>
                  <td>{user.username}</td>
                  <td>{user.role}</td>
                  <td>{user.playerId ?? "-"}</td>
                  <td>
                    {isSuperadmin ? (
                      <span style={{ color: "var(--text-muted)", fontSize: "0.85em" }}>
                        {t("zakulisi__superadminProtected")}
                      </span>
                    ) : user.role === "ADMIN" ? (
                      <Button
                        variant="outline-warning"
                        size="sm"
                        onClick={() => setRole(user, "USER")}
                        disabled={setRoleMutation.isPending}
                      >
                        {t("zakulisi__demoteToUser")}
                      </Button>
                    ) : (
                      <Button
                        variant="outline-success"
                        size="sm"
                        onClick={() => setRole(user, "ADMIN")}
                        disabled={setRoleMutation.isPending}
                      >
                        {t("zakulisi__promoteToAdmin")}
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
      {debouncedQuery && results && results.content.length === 0 && !isFetching && (
        <div className="text-center" style={{ color: "var(--text-muted)" }}>{t("auditLog__empty")}</div>
      )}
    </div>
  );
}

export default UserSection;
